import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/api/client'
import type { User } from '@/types/models'

interface AuthState {
  token: string | null
  userInfo: User | null
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  updateProfile: (data: Partial<User>) => Promise<void>
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      userInfo: null,
      isAuthenticated: false,

      login: async (username, password) => {
        try {
          const res = await api.post<{ ok: boolean; token: string; user: User }>(
            '/api/login',
            { username, password }
          )
          if (res.ok) {
            api.token = res.token
            set({
              token: res.token,
              userInfo: res.user,
              isAuthenticated: true,
            })
            return true
          }
          return false
        } catch (e) {
          throw e
        }
      },

      logout: () => {
        api.token = null
        set({ token: null, userInfo: null, isAuthenticated: false })
      },

      updateProfile: async (data) => {
        const res = await api.patch<{ ok: boolean; user?: User }>('/api/me', data)
        if (res.ok) {
          if (res.user) {
            set({ userInfo: res.user })
          } else {
            set(state => ({ userInfo: state.userInfo ? { ...state.userInfo, ...data } : null }))
          }
        }
      },

      refreshUser: async () => {
        try {
          const user = await api.get<User>('/api/me')
          if (user) set({ userInfo: user })
        } catch {
          // Token 过期或无效，自动登出
          api.token = null
          set({ token: null, userInfo: null, isAuthenticated: false })
        }
      },
    }),
    {
      name: 'cal_auth',
      partialize: (state) => ({
        token: state.token,
        userInfo: state.userInfo,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.token = state.token
          // 自动刷新用户信息
          state.refreshUser()
        }
      },
    }
  )
)
